import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  TrendingUp, TrendingDown, IndianRupee, RefreshCw, LogIn, LogOut, BedDouble,
  Search, Star, AlertCircle, Activity, Wallet, CalendarDays, Wifi, ChevronDown,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { toLocalISODate } from "../utils/date";
import { bookingLabel } from "../utils/bookingLabel";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

/* Channel-manager bookings carry the OTA's own name for itself ("BookingCom",
   "MMT"), which is not the same string the iCal feeds used. Both spellings
   are mapped so one OTA is one colour however the booking arrived. */
const SOURCE_COLORS = {
  Airbnb:        { text: "text-rose-600",    dot: "bg-rose-400",    hex: "#FB7185" },
  "Booking.com": { text: "text-blue-600",    dot: "bg-blue-400",    hex: "#60A5FA" },
  BookingCom:    { text: "text-blue-600",    dot: "bg-blue-400",    hex: "#60A5FA" },
  Agoda:         { text: "text-red-600",     dot: "bg-red-500",     hex: "#EF4444" },
  MakeMyTrip:    { text: "text-indigo-600",  dot: "bg-indigo-400",  hex: "#818CF8" },
  MMT:           { text: "text-indigo-600",  dot: "bg-indigo-400",  hex: "#818CF8" },
  Expedia:       { text: "text-amber-600",   dot: "bg-amber-400",   hex: "#FBBF24" },
  "Hotels.com":  { text: "text-orange-600",  dot: "bg-orange-400",  hex: "#FB923C" },
  Website:       { text: "text-emerald-600", dot: "bg-emerald-400", hex: "#34D399" },
  Direct:        { text: "text-emerald-600", dot: "bg-emerald-400", hex: "#34D399" },
  Manual:        { text: "text-purple-600",  dot: "bg-purple-400",  hex: "#C084FC" },
};
const sourceColor = s => SOURCE_COLORS[s] || { text: "text-gray-600", dot: "bg-gray-400", hex: "#9CA3AF" };

const money = n => `₹${Math.round(n || 0).toLocaleString("en-IN")}`;
const compactMoney = (n) => {
  const v = Math.round(n || 0);
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(1)}Cr`;
  if (v >= 100000)   return `₹${(v / 100000).toFixed(1)}L`;
  if (v >= 1000)     return `₹${Math.round(v / 1000)}k`;
  return `₹${v}`;
};

/* ── Animated counter ── */
function AnimCounter({ target, format, duration = 900 }) {
  const reduce = typeof window !== "undefined"
    && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const [value, setValue] = useState(reduce ? target : 0);
  const frame = useRef();

  useEffect(() => {
    if (reduce) { setValue(target); return; }
    const start = performance.now();
    const tick = now => {
      const t = Math.min(1, (now - start) / duration);
      setValue(target * (1 - Math.pow(1 - t, 3)));
      if (t < 1) frame.current = requestAnimationFrame(tick);
      else setValue(target);           // land exactly on the real number
    };
    frame.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame.current);
  }, [target, duration, reduce]);

  return <>{format ? format(value) : Math.round(value).toLocaleString("en-IN")}</>;
}

function Card({ children, className = "" }) {
  return (
    <div className={`bg-white rounded-2xl border border-gray-100 shadow-sm ${className}`}>
      {children}
    </div>
  );
}

function CardHead({ title, right }) {
  return (
    <div className="flex items-center justify-between gap-3 px-4 sm:px-5 pt-4 pb-3">
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      {right}
    </div>
  );
}

/* A time-range picker styled as a pill. A native <select> underneath on
   purpose: on a phone it opens the OS picker, which is larger, faster and
   more accessible than any dropdown we could draw, and it's 44px tall. */
function RangeSelect({ value, onChange, options, label }) {
  return (
    <div className="relative shrink-0">
      <select value={value} onChange={e => onChange(Number(e.target.value))} aria-label={label}
        className="appearance-none min-h-[44px] pl-3.5 pr-8 text-xs font-medium text-gray-700 bg-gray-50
          border border-gray-200 rounded-full cursor-pointer hover:bg-gray-100 transition-colors
          focus:outline-none focus:ring-2 focus:ring-amber-400">
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <ChevronDown size={13} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
    </div>
  );
}

const MONTH_RANGES = [
  { value: 3,  label: "Last 3 months" },
  { value: 6,  label: "Last 6 months" },
  { value: 12, label: "Last 12 months" },
];
/* Capped at 30: /calendar returns stays from 30 days back, so a longer
   window would silently show empty history as zero occupancy. */
const DAY_RANGES = [
  { value: 7,  label: "Last 7 days" },
  { value: 14, label: "Last 14 days" },
  { value: 30, label: "Last 30 days" },
];

/**
 * Width of an element in CSS pixels, kept current as it resizes.
 *
 * A CALLBACK ref, not useRef + a mount-only effect. The charts first render
 * a "No revenue yet" placeholder while their data is still in flight, so
 * the measured element doesn't exist on mount — it appears a moment later.
 * A mount-only effect found nothing, gave up, and never looked again: the
 * chart then had width 0 and drew nothing, while the numbers under it were
 * fine. Whether you saw it depended on which request won the race. With a
 * callback ref the measuring starts whenever the element actually appears.
 */
function useWidth() {
  const [el, setEl] = useState(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!el) return;
    // Measure immediately rather than waiting a frame for the observer, and
    // so a browser without ResizeObserver still gets a width.
    setWidth(Math.floor(el.getBoundingClientRect().width));
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([entry]) => setWidth(Math.floor(entry.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);
  return [setEl, width];
}

/* Show every label when they fit, otherwise every k-th — always keeping the
   last, which is the most recent and the one people look for. */
const labelEvery = (count, width, minGap = 40) => Math.max(1, Math.ceil(count / Math.max(1, Math.floor(width / minGap))));
const showLabel = (i, count, every) => (count - 1 - i) % every === 0;

/* ── Charts, drawn by hand ───────────────────────────────────────────────
   No charting library: the smallest credible one is around 100KB gzipped
   against a bundle that has to install as a PWA and open on a phone
   (CLAUDE.md I7), for two charts whose whole job is one path and a handful
   of rectangles.

   Drawn at the container's REAL width, measured, rather than on a fixed
   canvas scaled to fit. Scaling a 600-wide drawing into a 300px phone card
   halves everything in it — the axis labels came out about 5px tall — and
   leaves dead bands above and below the plot. Measured, an 11px label is
   11px on every screen. */

function AreaChart({ points, height = 170 }) {
  const [ref, w] = useWidth();
  const h = height, pad = { t: 14, r: 14, b: 26, l: 14 };
  if (!points.length) return <p className="text-xs text-gray-400 px-5 pb-5">No revenue yet.</p>;

  const max = Math.max(...points.map(p => p.value), 1);
  const innerW = Math.max(1, w - pad.l - pad.r);
  const innerH = h - pad.t - pad.b;
  const x = i => pad.l + (points.length === 1 ? innerW / 2 : (i / (points.length - 1)) * innerW);
  const y = v => pad.t + innerH - (v / max) * innerH;

  /* Catmull-Rom → cubic Bézier, so the line reads as a trend rather than a
     zigzag. Straight segments below three points, where a curve would
     invent movement that isn't in the data. */
  const at = i => points[Math.max(0, Math.min(points.length - 1, i))];
  const line = points.length < 3
    ? points.map((p, i) => `${i ? "L" : "M"}${x(i)},${y(p.value)}`).join(" ")
    : points.reduce((d, p, i) => {
        if (!i) return `M${x(0)},${y(p.value)}`;
        const c1x = x(i - 1) + (x(i) - x(Math.max(0, i - 2))) / 6;
        const c1y = y(at(i - 1).value) + (y(p.value) - y(at(i - 2).value)) / 6;
        const c2x = x(i) - (x(Math.min(points.length - 1, i + 1)) - x(i - 1)) / 6;
        const c2y = y(p.value) - (y(at(i + 1).value) - y(at(i - 1).value)) / 6;
        return `${d} C${c1x},${c1y} ${c2x},${c2y} ${x(i)},${y(p.value)}`;
      }, "");
  const every = labelEvery(points.length, innerW, 38);

  return (
    <div ref={ref} className="px-1.5 sm:px-2" style={{ height }}>
      {w > 0 && (
        <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} role="img"
          aria-label={`Revenue for the last ${points.length} months`}>
          <defs>
            <linearGradient id="hs-area" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#F59E0B" stopOpacity="0.28" />
              <stop offset="100%" stopColor="#F59E0B" stopOpacity="0" />
            </linearGradient>
          </defs>
          {[0.25, 0.5, 0.75, 1].map(f => (
            <line key={f} x1={pad.l} x2={w - pad.r} y1={pad.t + innerH * f} y2={pad.t + innerH * f}
              stroke="#F3F4F6" strokeWidth="1" />
          ))}
          <path d={`${line} L${x(points.length - 1)},${pad.t + innerH} L${x(0)},${pad.t + innerH} Z`} fill="url(#hs-area)" />
          <path d={line} fill="none" stroke="#F59E0B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          {points.map((p, i) => (
            <g key={`${p.label}-${i}`}>
              <circle cx={x(i)} cy={y(p.value)} r={i === points.length - 1 ? 4.5 : 3}
                fill="#fff" stroke="#F59E0B" strokeWidth="2" />
              {showLabel(i, points.length, every) && (
                <text x={x(i)} y={h - 7} textAnchor="middle" fontSize="11" fill="#9CA3AF">{p.label}</text>
              )}
              <title>{`${p.label}: ${money(p.value)}`}</title>
            </g>
          ))}
        </svg>
      )}
    </div>
  );
}

function BarChart({ bars, height = 160 }) {
  const [ref, w] = useWidth();
  const h = height, pad = { t: 10, b: 26, x: 6 };
  if (!bars.length) return <p className="text-xs text-gray-400 px-5 pb-5">No data yet.</p>;
  const innerH = h - pad.t - pad.b;
  const slot = Math.max(1, (w - pad.x * 2) / bars.length);
  const bw = Math.max(3, Math.min(30, slot * 0.58));
  const every = labelEvery(bars.length, w, 30);

  return (
    <div ref={ref} className="px-1.5 sm:px-2" style={{ height }}>
      {w > 0 && (
        <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} role="img"
          aria-label={`Occupancy over the last ${bars.length} nights`}>
          {bars.map((b, i) => {
            const cx = pad.x + slot * i + slot / 2;
            const bh = Math.max(2, (b.pct / 100) * innerH);
            const r = Math.min(6, bw / 2);
            return (
              <g key={`${b.label}-${i}`}>
                <rect x={cx - bw / 2} y={pad.t} width={bw} height={innerH} rx={r} fill="#F9FAFB" />
                <rect x={cx - bw / 2} y={pad.t + innerH - bh} width={bw} height={bh} rx={r}
                  fill={b.pct >= 80 ? "#F59E0B" : b.pct >= 40 ? "#FBBF24" : "#FCD34D"} />
                {showLabel(i, bars.length, every) && (
                  <text x={cx} y={h - 8} textAnchor="middle" fontSize="11" fill="#9CA3AF">{b.label}</text>
                )}
                <title>{`${b.title}: ${b.pct}% (${b.booked}/${b.total})`}</title>
              </g>
            );
          })}
        </svg>
      )}
    </div>
  );
}

function Donut({ slices, total }) {
  const size = 132, stroke = 16, r = (size - stroke) / 2, c = 2 * Math.PI * r;
  let offset = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0" role="img"
      aria-label="Bookings by source">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#F3F4F6" strokeWidth={stroke} />
      {total > 0 && slices.map(s => {
        const len = (s.value / total) * c;
        const el = (
          <circle key={s.label} cx={size / 2} cy={size / 2} r={r} fill="none"
            stroke={s.hex} strokeWidth={stroke} strokeDasharray={`${len} ${c - len}`}
            strokeDashoffset={-offset} transform={`rotate(-90 ${size / 2} ${size / 2})`}>
            <title>{`${s.label}: ${Math.round((s.value / total) * 100)}%`}</title>
          </circle>
        );
        offset += len;
        return el;
      })}
      <text x="50%" y="47%" textAnchor="middle" fontSize="19" fontWeight="700" fill="#111827">{total}</text>
      <text x="50%" y="62%" textAnchor="middle" fontSize="10" fill="#9CA3AF">bookings</text>
    </svg>
  );
}

/* ── Room status strip — one bar per physical unit, real states only ── */
const UNIT_STATE = {
  occupied: { label: "Occupied",  cls: "bg-amber-500", dot: "bg-amber-500" },
  arriving: { label: "Arriving",  cls: "bg-amber-300", dot: "bg-amber-300" },
  notReady: { label: "Not ready", cls: "bg-rose-300",  dot: "bg-rose-300" },
  free:     { label: "Available", cls: "bg-gray-200",  dot: "bg-gray-300" },
};

function RoomStrip({ units }) {
  const counts = units.reduce((a, u) => ({ ...a, [u.state]: (a[u.state] || 0) + 1 }), {});
  return (
    <div className="px-4 sm:px-5 pb-5">
      <div className="flex items-baseline justify-between mb-3">
        <span className="text-xs text-gray-400">Rooms tonight</span>
        <span className="text-xl font-bold text-gray-900 tabular-nums">{units.length}</span>
      </div>
      <div className="flex gap-[3px] flex-wrap mb-4">
        {units.map(u => (
          <span key={u.key} title={`${u.name} — ${UNIT_STATE[u.state].label}`}
            className={`h-9 flex-1 min-w-[5px] max-w-[18px] rounded-full ${UNIT_STATE[u.state].cls}`} />
        ))}
        {!units.length && <span className="text-xs text-gray-400">No rooms set up yet.</span>}
      </div>
      <div className="grid grid-cols-2 gap-y-2">
        {Object.entries(UNIT_STATE).map(([key, meta]) => (
          <div key={key} className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${meta.dot}`} />
            <span className="text-xs text-gray-500">{meta.label}</span>
            <span className="text-xs font-semibold text-gray-900 tabular-nums">{counts[key] || 0}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* Three across even on a 360px phone. Stacked, each tile took a full
   screen-width row that was mostly empty, and a phone scrolled past three
   screens of white before reaching a single chart. The subtitle (a guest's
   name) needs width a third of a phone doesn't have, so it appears from
   the tablet breakpoint up. */
function KpiTile({ icon: Icon, label, value, sub, tone = "gray" }) {
  const tones = {
    amber:   "bg-amber-50 text-amber-600",
    emerald: "bg-emerald-50 text-emerald-600",
    blue:    "bg-blue-50 text-blue-600",
    gray:    "bg-gray-100 text-gray-500",
  };
  return (
    <Card className="p-3 sm:p-4 min-w-0">
      <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-xl flex items-center justify-center mb-2 sm:mb-3 ${tones[tone]}`}>
        <Icon size={16} />
      </div>
      <p className="text-xl sm:text-2xl font-bold text-gray-900 tabular-nums leading-none">
        <AnimCounter target={value} />
      </p>
      <p className="text-[11px] sm:text-[13px] font-medium text-gray-700 mt-1.5 leading-tight">{label}</p>
      {sub && <p className="hidden sm:block text-[11px] text-gray-400 mt-0.5 truncate">{sub}</p>}
    </Card>
  );
}

const REVENUE_VIEWS = [
  { id: "month", label: "Month" },
  { id: "year",  label: "Year" },
  { id: "all",   label: "All" },
];

const STATUS_STYLE = {
  Confirmed: "bg-emerald-50 text-emerald-700",
  Pending:   "bg-amber-50 text-amber-700",
  Completed: "bg-gray-100 text-gray-600",
  Cancelled: "bg-rose-50 text-rose-600",
  Blocked:   "bg-slate-100 text-slate-600",
};

export default function Dashboard() {
  const { authHeaders } = useAuth();
  const { hotelName } = useSettings();

  const [bookings, setBookings]     = useState([]);
  const [rooms, setRooms]           = useState([]);
  const [financials, setFinancials] = useState(null);
  const [financialsAllTime, setFinancialsAllTime] = useState(null);
  const [financialsMonth, setFinancialsMonth]     = useState(null);
  const [monthly, setMonthly]       = useState([]);
  const [activity, setActivity]     = useState([]);
  const [reviews, setReviews]       = useState([]);
  const [syncStatus, setSyncStatus] = useState([]);
  const [totalBookingCount, setTotalBookingCount] = useState(0);
  const [revenueView, setRevenueView] = useState("year");
  const [revenueMonths, setRevenueMonths] = useState(6);
  const [occupancyDays, setOccupancyDays] = useState(7);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [query, setQuery]           = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading]       = useState(true);

  /* Read-only by construction: every call below is a GET. The dashboard
     cannot create, move, or cancel a booking, and so cannot touch
     availability or anything the channel manager is told. */
  useEffect(() => {
    async function load() {
      // Every panel is optional. A feature flag off or a role without the
      // permission returns 403, and that must empty one card rather than
      // blank the whole dashboard.
      const get = async (path) => {
        try {
          const res = await fetch(`${API}${path}`, { headers: authHeaders() });
          if (!res.ok) return null;
          return await res.json();
        } catch { return null; }
      };

      const now = new Date();
      const monthFrom = toLocalISODate(new Date(now.getFullYear(), now.getMonth(), 1));
      const monthTo   = toLocalISODate(new Date(now.getFullYear(), now.getMonth() + 1, 0));

      const [cal, fin, finAll, finMonth, rms, notif, revs, sync, count] = await Promise.all([
        get("/calendar"),
        get("/financials"),
        get("/financials?all=true"),
        get(`/financials?from=${monthFrom}&to=${monthTo}`),
        get("/rooms"),
        get("/notifications?limit=8"),
        // Published only: the rating here must be the one guests see.
        get("/reviews?approved=true"),
        get("/sync/status"),
        // The same unfiltered count the Bookings page shows as "N total", so
        // the two screens can never report different numbers.
        get("/bookings?limit=1"),
      ]);

      setBookings(cal?.data || []);
      setFinancials(fin?.data || null);
      setFinancialsAllTime(finAll?.data || null);
      setFinancialsMonth(finMonth?.data || null);
      setRooms(rms?.data || []);
      setActivity(notif?.data || []);
      setReviews(revs?.data || []);
      setSyncStatus(sync?.data || []);
      setTotalBookingCount(count?.total ?? 0);
      setLoading(false);
    }
    load();
  }, []);

  // Always at least 3 months, whatever the chart shows: the trend below
  // compares the last two COMPLETE months, which needs the current partial
  // one plus two behind it.
  useEffect(() => {
    let live = true;
    setMonthlyLoading(true);
    fetch(`${API}/financials/monthly?months=${Math.max(3, revenueMonths)}`, { headers: authHeaders() })
      .then(r => (r.ok ? r.json() : null))
      .then(j => { if (live) setMonthly(j?.data || []); })
      .catch(() => { if (live) setMonthly([]); })
      .finally(() => { if (live) setMonthlyLoading(false); });
    return () => { live = false; };
  }, [revenueMonths]);

  const today = useMemo(() => toLocalISODate(new Date()), []);
  const dayOf = d => toLocalISODate(new Date(d));

  const arrivals   = bookings.filter(b => dayOf(b.checkIn) === today && b.status !== "Cancelled");
  const departures = bookings.filter(b => dayOf(b.checkOut) === today && b.status !== "Cancelled");
  const inHouse    = bookings.filter(b =>
    dayOf(b.checkIn) <= today && dayOf(b.checkOut) > today && ["Confirmed", "Completed"].includes(b.status));

  /* Tonight, per physical unit. Occupied if someone is staying in it,
     arriving if tonight is their check-in, not ready if housekeeping says
     so — the same field the housekeeping board reads, so the two screens
     cannot disagree. */
  const units = useMemo(() => {
    const held = new Map();
    for (const b of bookings) {
      if (!["Confirmed", "Completed", "Pending", "Blocked"].includes(b.status)) continue;
      if (!(dayOf(b.checkIn) <= today && dayOf(b.checkOut) > today)) continue;
      if (b.unitId) held.set(String(b.unitId), dayOf(b.checkIn) === today ? "arriving" : "occupied");
    }
    return rooms.flatMap(r => (r.units || []).filter(u => u.active !== false).map(u => {
      const id = String(u._id);
      const hk = u.housekeepingStatus;
      const state = held.get(id) || (hk === "dirty" || hk === "out_of_order" ? "notReady" : "free");
      return { key: id, name: `${r.name} ${u.roomNumber || ""}`.trim(), state };
    }));
  }, [rooms, bookings, today]);

  /* Each of the last N nights, against the same unit count the strip uses,
     so the two never tell different stories. Weekday initials for a week;
     day-of-month once there are more bars than a week's worth of names. */
  const occupancy = useMemo(() => {
    const total = units.length;
    return Array.from({ length: occupancyDays }, (_, i) => {
      const d = new Date();
      d.setDate(d.getDate() - (occupancyDays - 1 - i));
      const key = toLocalISODate(d);
      const booked = new Set(bookings
        .filter(b => ["Confirmed", "Completed", "Blocked"].includes(b.status)
          && dayOf(b.checkIn) <= key && dayOf(b.checkOut) > key && b.unitId)
        .map(b => String(b.unitId))).size;
      return {
        label: occupancyDays <= 7
          ? d.toLocaleDateString("en-IN", { weekday: "short" }).slice(0, 2)
          : String(d.getDate()),
        title: d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" }),
        pct: total ? Math.round((booked / total) * 100) : 0, booked, total,
      };
    });
  }, [bookings, units.length, occupancyDays]);

  const revenueSource =
    revenueView === "all" ? financialsAllTime : revenueView === "month" ? financialsMonth : financials;
  const totalRevenue = revenueSource?.totalRevenue || 0;
  const pendingBalance = (financialsAllTime?.totalRevenue || 0) - (financialsAllTime?.totalAdvance || 0);

  /* The last COMPLETE month against the one before it. Comparing the
     current month-to-date with last month's full total reads as a collapse
     every time a month begins — on the 2nd it would show a ~95% "drop" that
     is only the calendar — so the partial month is left out entirely. */
  const trend = useMemo(() => {
    if (monthly.length < 3) return null;
    const [prev, last] = monthly.slice(-3, -1);
    if (!prev.revenue) return null;
    return { pct: Math.round(((last.revenue - prev.revenue) / prev.revenue) * 1000) / 10, last: last.label, prev: prev.label };
  }, [monthly]);
  const chartMonths = monthly.slice(-revenueMonths);

  const sources = useMemo(() => {
    const by = revenueSource?.byPlatform || {};
    return Object.entries(by)
      .map(([label, v]) => ({ label, value: v.totalBookings || 0, hex: sourceColor(label).hex }))
      .filter(s => s.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [revenueSource]);
  const sourceTotal = sources.reduce((s, x) => s + x.value, 0);

  const rating = useMemo(() => {
    const rated = reviews.filter(r => r.rating);
    if (!rated.length) return null;
    const avg = rated.reduce((s, r) => s + r.rating, 0) / rated.length;
    const dist = [5, 4, 3, 2, 1].map(star => ({
      star, count: rated.filter(r => Math.round(r.rating) === star).length,
    }));
    return { avg: Math.round(avg * 10) / 10, count: rated.length, dist };
  }, [reviews]);

  /* What a person should act on, derived from the data rather than kept as
     a to-do list — a list nobody maintains is worse than no list. */
  const attention = useMemo(() => {
    const items = [];
    const pending = bookings.filter(b => b.status === "Pending");
    if (pending.length) items.push({ key: "pending", tone: "amber",
      text: `${pending.length} booking${pending.length > 1 ? "s" : ""} awaiting approval` });
    const unassigned = bookings.filter(b => !b.unitId && ["Confirmed", "Pending"].includes(b.status)
      && dayOf(b.checkOut) > today);
    if (unassigned.length) items.push({ key: "unassigned", tone: "rose",
      text: `${unassigned.length} upcoming booking${unassigned.length > 1 ? "s" : ""} with no room assigned` });
    const owing = inHouse.filter(b => (b.payment?.remainingBalance || 0) > 0);
    if (owing.length) items.push({ key: "owing", tone: "amber",
      text: `${owing.length} in-house guest${owing.length > 1 ? "s" : ""} with a balance due` });
    const notReady = units.filter(u => u.state === "notReady").length;
    if (notReady) items.push({ key: "hk", tone: "rose",
      text: `${notReady} room${notReady > 1 ? "s" : ""} not ready` });
    return items;
  }, [bookings, inHouse, units, today]);

  const visibleBookings = useMemo(() => {
    const q = query.trim().toLowerCase();
    return bookings
      .filter(b => statusFilter === "all" || b.status === statusFilter)
      .filter(b => !q
        || bookingLabel(b).toLowerCase().includes(q)
        || (b.bookingRef || "").toLowerCase().includes(q)
        || (b.source || "").toLowerCase().includes(q))
      .sort((a, b) => {
        const aUp = dayOf(a.checkOut) > today, bUp = dayOf(b.checkOut) > today;
        if (aUp !== bUp) return aUp ? -1 : 1;
        return aUp ? new Date(a.checkIn) - new Date(b.checkIn) : new Date(b.checkIn) - new Date(a.checkIn);
      })
      .slice(0, 8);
  }, [bookings, query, statusFilter, today]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw size={24} className="animate-spin text-amber-500" />
          <p className="text-sm text-gray-500">Loading dashboard…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-[1500px] mx-auto">
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_340px] gap-4 sm:gap-5">
        {/* ══ Main column ══ */}
        <div className="space-y-4 sm:space-y-5 min-w-0">

          {/* Revenue + today's movements */}
          <div className="grid grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            <Card className="p-5 bg-gradient-to-br from-amber-500 to-orange-500 border-0 text-white col-span-3 lg:col-span-2">
              <p className="text-[13px] font-medium text-white/95 truncate">{hotelName || "Your property"}</p>
              <p className="text-[11px] text-white/70 mb-4">
                {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}
              </p>
              <div className="flex items-center gap-2 mb-1.5">
                <Wallet size={14} className="text-white/80" />
                <span className="text-[11px] font-medium text-white/80">Revenue</span>
              </div>
              <p className="text-[28px] leading-none font-bold tabular-nums tracking-tight">
                <AnimCounter target={totalRevenue} format={money} />
              </p>
              {/* Named months, so it's clear this is a finished month against
                  the one before — not a partial month against a full one. */}
              {trend && (
                <p className="flex items-center gap-1 text-[11px] font-medium text-white/85 mt-2">
                  {trend.pct >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                  {trend.pct > 0 ? "+" : ""}{trend.pct}% in {trend.last} vs {trend.prev}
                </p>
              )}
              <div className="flex gap-1 mt-4">
                {REVENUE_VIEWS.map(v => (
                  <button key={v.id} onClick={() => setRevenueView(v.id)}
                    className={`flex-1 min-h-[44px] text-xs font-semibold rounded-xl transition-colors
                      ${revenueView === v.id ? "bg-white text-orange-600" : "bg-white/15 text-white hover:bg-white/25"}`}>
                    {v.label}
                  </button>
                ))}
              </div>
            </Card>

            <KpiTile icon={LogIn} tone="emerald" label="Arriving today" value={arrivals.length}
              sub={arrivals.length ? bookingLabel(arrivals[0]) : "nobody expected"} />
            <KpiTile icon={BedDouble} tone="amber" label="Staying tonight" value={inHouse.length}
              sub={`${units.filter(u => u.state !== "free").length} of ${units.length} rooms held`} />
            <KpiTile icon={LogOut} tone="blue" label="Departing today" value={departures.length}
              sub={departures.length ? bookingLabel(departures[0]) : "nobody leaving"} />
          </div>

          {/* Revenue trend + occupancy */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Card>
              <CardHead title="Revenue trend"
                right={<RangeSelect label="Revenue period" value={revenueMonths} onChange={setRevenueMonths} options={MONTH_RANGES} />} />
              <div className={`transition-opacity ${monthlyLoading ? "opacity-40" : ""}`}>
                <AreaChart points={chartMonths.map(m => ({ label: m.label, value: m.revenue }))} />
              </div>
              <div className="flex items-center justify-between gap-3 px-4 sm:px-5 pb-4 pt-1 text-[11px] text-gray-400">
                <span>Best month {compactMoney(Math.max(0, ...chartMonths.map(m => m.revenue)))}</span>
                <span className="text-right">{chartMonths.reduce((s, m) => s + m.bookings, 0)} bookings in the period</span>
              </div>
            </Card>

            <Card>
              <CardHead title="Occupancy"
                right={<RangeSelect label="Occupancy period" value={occupancyDays} onChange={setOccupancyDays} options={DAY_RANGES} />} />
              <BarChart bars={occupancy} />
              <div className="px-4 sm:px-5 pb-4 pt-1 text-[11px] text-gray-400">
                Averaging {Math.round(occupancy.reduce((s, o) => s + o.pct, 0) / (occupancy.length || 1))}%
                {" "}across {units.length} room{units.length !== 1 ? "s" : ""}
              </div>
            </Card>
          </div>

          {/* Booking source + ratings */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Card>
              <CardHead title="Where bookings come from"
                right={<span className="text-[11px] text-gray-400 capitalize">{revenueView === "all" ? "all time" : `this ${revenueView}`}</span>} />
              <div className="flex items-center gap-4 sm:gap-5 px-4 sm:px-5 pb-5">
                <Donut slices={sources} total={sourceTotal} />
                <div className="flex-1 min-w-0 space-y-2.5">
                  {!sources.length && <p className="text-xs text-gray-400">No bookings in this period.</p>}
                  {sources.slice(0, 5).map(s => (
                    <div key={s.label}>
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="flex items-center gap-1.5 min-w-0">
                          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: s.hex }} />
                          <span className="text-xs text-gray-600 truncate">{s.label}</span>
                        </span>
                        <span className="text-xs font-semibold text-gray-900 tabular-nums shrink-0">
                          {Math.round((s.value / sourceTotal) * 100)}%
                        </span>
                      </div>
                      <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${(s.value / sourceTotal) * 100}%`, background: s.hex }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>

            <Card>
              <CardHead title="Guest ratings"
                right={rating && <span className="text-[11px] text-gray-400">{rating.count} published review{rating.count !== 1 ? "s" : ""}</span>} />
              {!rating ? (
                <p className="text-xs text-gray-400 px-5 pb-5">No reviews yet — they appear here once guests leave one.</p>
              ) : (
                <div className="flex items-center gap-4 sm:gap-5 px-4 sm:px-5 pb-5">
                  <div className="text-center shrink-0">
                    <p className="text-4xl font-bold text-gray-900 tabular-nums leading-none">{rating.avg}</p>
                    <div className="flex gap-0.5 justify-center mt-2">
                      {[1, 2, 3, 4, 5].map(i => (
                        <Star key={i} size={13}
                          className={i <= Math.round(rating.avg) ? "text-amber-400 fill-amber-400" : "text-gray-200 fill-gray-200"} />
                      ))}
                    </div>
                    <p className="text-[11px] text-gray-400 mt-1.5">out of 5</p>
                  </div>
                  <div className="flex-1 min-w-0 space-y-1.5">
                    {rating.dist.map(d => (
                      <div key={d.star} className="flex items-center gap-2">
                        <span className="text-[11px] text-gray-400 w-3 tabular-nums">{d.star}</span>
                        <Star size={10} className="text-amber-400 fill-amber-400 shrink-0" />
                        <div className="flex-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                          <div className="h-full rounded-full bg-amber-400"
                            style={{ width: `${(d.count / rating.count) * 100}%` }} />
                        </div>
                        <span className="text-[11px] text-gray-500 tabular-nums w-4 text-right">{d.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          </div>

          {/* Bookings */}
          <Card>
            <div className="flex items-center justify-between gap-3 px-4 sm:px-5 pt-4 pb-3 flex-wrap">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Bookings</h3>
                <p className="text-[11px] text-gray-400">
                  {totalBookingCount.toLocaleString("en-IN")} all-time · showing the last 30 days and next 90, upcoming first
                </p>
              </div>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <div className="relative flex-1 sm:w-56">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" />
                  <input value={query} onChange={e => setQuery(e.target.value)}
                    placeholder="Search bookings"
                    className="w-full min-h-[44px] pl-9 pr-3 text-xs border border-gray-200 rounded-xl
                      focus:outline-none focus:ring-2 focus:ring-amber-400" />
                </div>
                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                  aria-label="Filter by status"
                  className="min-h-[44px] px-2.5 text-xs border border-gray-200 rounded-xl bg-white
                    focus:outline-none focus:ring-2 focus:ring-amber-400">
                  <option value="all">All status</option>
                  {["Confirmed", "Pending", "Completed", "Blocked"].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            {/* A phone gets a list, not a table: five columns cannot share
                320px, and a sideways-scrolling table clipped the one column
                that matters most — the status. */}
            <ul className="sm:hidden divide-y divide-gray-50 border-t border-gray-100">
              {!visibleBookings.length && <li className="px-4 py-8 text-center text-sm text-gray-400">No bookings match.</li>}
              {visibleBookings.map(b => (
                <li key={b._id} className="flex items-start justify-between gap-3 px-4 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{bookingLabel(b)}</p>
                    <p className="text-xs text-gray-600 mt-0.5">
                      {new Date(b.checkIn).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                      {" – "}
                      {new Date(b.checkOut).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                      {b.room?.name ? <span className="text-gray-400"> · {b.room.name}</span> : null}
                    </p>
                    <p className={`text-[11px] flex items-center gap-1.5 mt-0.5 ${sourceColor(b.source).text}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${sourceColor(b.source).dot}`} />
                      {b.source || "—"}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <span className={`inline-block text-[11px] font-semibold px-2 py-1 rounded-full whitespace-nowrap ${STATUS_STYLE[b.status] || "bg-gray-100 text-gray-600"}`}>
                      {b.status}
                    </span>
                    <p className="text-xs tabular-nums text-gray-900 mt-1">{money(b.payment?.totalAmount)}</p>
                  </div>
                </li>
              ))}
            </ul>
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wider text-gray-400 border-y border-gray-100">
                    <th className="text-left font-semibold px-5 py-2.5">Guest</th>
                    <th className="text-left font-semibold px-3 py-2.5 hidden sm:table-cell">Room</th>
                    <th className="text-left font-semibold px-3 py-2.5">Dates</th>
                    <th className="text-right font-semibold px-3 py-2.5 hidden md:table-cell">Total</th>
                    <th className="text-right font-semibold px-5 py-2.5">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {!visibleBookings.length && (
                    <tr><td colSpan={5} className="px-5 py-8 text-center text-sm text-gray-400">No bookings match.</td></tr>
                  )}
                  {visibleBookings.map(b => (
                    <tr key={b._id} className="hover:bg-gray-50/70 transition-colors">
                      <td className="px-5 py-3">
                        <p className="font-medium text-gray-900 truncate max-w-[170px]">{bookingLabel(b)}</p>
                        <p className={`text-[11px] flex items-center gap-1.5 ${sourceColor(b.source).text}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${sourceColor(b.source).dot}`} />
                          {b.source || "—"}
                        </p>
                      </td>
                      <td className="px-3 py-3 text-gray-600 hidden sm:table-cell">
                        {b.room?.name || "—"}{b.unitRoomNumber ? ` · ${b.unitRoomNumber}` : ""}
                      </td>
                      <td className="px-3 py-3 text-gray-600 whitespace-nowrap text-xs">
                        {new Date(b.checkIn).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                        {" – "}
                        {new Date(b.checkOut).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums text-gray-900 hidden md:table-cell">
                        {money(b.payment?.totalAmount)}
                      </td>
                      <td className="px-5 py-3 text-right">
                        <span className={`text-[11px] font-semibold px-2 py-1 rounded-full whitespace-nowrap ${STATUS_STYLE[b.status] || "bg-gray-100 text-gray-600"}`}>
                          {b.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        {/* ══ Right rail ══ */}
        <div className="space-y-5 min-w-0">
          <Card>
            <CardHead title="Room status" />
            <RoomStrip units={units} />
          </Card>

          <Card>
            <CardHead title="Needs attention" />
            <div className="px-4 sm:px-5 pb-5 space-y-2">
              {!attention.length && <p className="text-xs text-gray-400">Nothing needs you right now.</p>}
              {attention.map(a => (
                <div key={a.key} className={`flex items-start gap-2.5 rounded-xl px-3 py-2.5
                  ${a.tone === "rose" ? "bg-rose-50" : "bg-amber-50"}`}>
                  <AlertCircle size={14} className={`mt-0.5 shrink-0 ${a.tone === "rose" ? "text-rose-500" : "text-amber-500"}`} />
                  <span className={`text-xs ${a.tone === "rose" ? "text-rose-800" : "text-amber-800"}`}>{a.text}</span>
                </div>
              ))}
            </div>
          </Card>

          <Card>
            <CardHead title="Recent activity" />
            <div className="px-4 sm:px-5 pb-5">
              {!activity.length && <p className="text-xs text-gray-400">Nothing yet.</p>}
              <div className="space-y-3.5">
                {activity.slice(0, 8).map(n => (
                  <div key={n._id} className="flex gap-2.5">
                    <span className="w-7 h-7 rounded-lg bg-gray-100 text-gray-500 flex items-center justify-center shrink-0 mt-0.5">
                      <Activity size={13} />
                    </span>
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-900 leading-snug">{n.title}</p>
                      {n.message && <p className="text-[11px] text-gray-500 leading-snug mt-0.5 line-clamp-2">{n.message}</p>}
                      <p className="text-[10px] text-gray-400 mt-0.5 flex items-center gap-1">
                        <CalendarDays size={9} />
                        {new Date(n.createdAt).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          {syncStatus.some(f => f.active !== false) && (
            <Card>
              <CardHead title="Calendar feeds" right={<Wifi size={14} className="text-gray-300" />} />
              <div className="px-4 sm:px-5 pb-5 space-y-2.5">
                {syncStatus.filter(f => f.active !== false).map(f => {
                  const ago = f.lastSyncAt ? Math.round((Date.now() - new Date(f.lastSyncAt)) / 60000) : null;
                  /* Failing NOW means the latest recorded error is newer than
                     the latest good sync. An error from last week that has
                     since been followed by clean syncs is history, not a
                     problem. */
                  const lastErr = (f.syncErrors || []).reduce((a, e) => (!a || new Date(e.at) > new Date(a.at) ? e : a), null);
                  const failing = !!lastErr && (!f.lastSyncAt || new Date(lastErr.at) > new Date(f.lastSyncAt));
                  const stale = failing || ago === null || ago > 60;
                  return (
                    <div key={f._id} className="flex items-center justify-between gap-2"
                      title={failing ? lastErr.message : undefined}>
                      <span className="flex items-center gap-2 min-w-0">
                        <span className={`w-2 h-2 rounded-full shrink-0 ${stale ? "bg-rose-400" : "bg-emerald-400"}`} />
                        <span className="text-xs text-gray-700 truncate">
                          {f.platform}{f.room?.name ? ` · ${f.room.name}` : ""}
                        </span>
                      </span>
                      <span className={`text-[11px] shrink-0 ${stale ? "text-rose-600" : "text-gray-400"}`}>
                        {failing ? "failing" : ago === null ? "never synced"
                          : ago < 1 ? "just now" : ago < 60 ? `${ago}m ago` : `${Math.round(ago / 60)}h ago`}
                      </span>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          <Card className="p-5">
            <div className="flex items-center gap-2 mb-1">
              <IndianRupee size={14} className="text-gray-400" />
              <span className="text-xs font-medium text-gray-500">Still to collect</span>
            </div>
            <p className="text-2xl font-bold text-gray-900 tabular-nums">
              <AnimCounter target={Math.max(0, pendingBalance)} format={money} />
            </p>
            {/* Same formula as the Financials page, so the two can never
                disagree: booked value minus every payment recorded against
                it, all bookings ever, cancellations excluded. */}
            <p className="text-[11px] text-gray-400 mt-1 leading-snug">
              Booked amount minus payments recorded, across all bookings. Money taken but not
              recorded in the PMS still counts as owed here.
            </p>
          </Card>
        </div>
      </div>
    </div>
  );
}
